// AddCategory.jsx — Web version synced with mobile logic (NO image, Edit + New + Listing)
import React, { useEffect, useMemo, useState } from "react";

const BRAND = "#015b9a";
const AddCategory = () => {
  const [categoryList, setCategoryList] = useState([]);
  const [categoryName, setCategoryName] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [loading, setLoading] = useState(true);

  // bottom sheet (add / edit)
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState("add"); // 'add' | 'edit'
  const [submitting, setSubmitting] = useState(false);

  // edit state
  const [selectedId, setSelectedId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editStatus, setEditStatus] = useState(1); // 1=active,0=inactive

  const [confirmId, setConfirmId] = useState(null);
  const [toast, setToast] = useState(null); // {type:'success'|'error'|'info', msg:string}

  const userData = JSON.parse(localStorage.getItem("userData"))?.data;
  const restaurantid = userData?.id;

  // -------- Helpers --------
  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2500);
  };

  // debounce search text
  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedQuery(searchQuery.trim().toLowerCase()),
      200
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const filteredList = useMemo(() => {
    if (!debouncedQuery) return categoryList;
    return (categoryList || []).filter((item) =>
      String(item.productcategoryname || "")
        .toLowerCase()
        .includes(debouncedQuery)
    );
  }, [categoryList, debouncedQuery]);

  // -------- Effects --------
  useEffect(() => {
    fetchCategoriesMaster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------- API --------
  const fetchCategories = async () => {
    if (!restaurantid) {
      showToast("error", "Restaurant not found. Please login again.");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(
        "https://qr.billsarthi.in/public/api/productcategorylistformaster",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid }),
        }
      );
      const data = await res.json();
      setCategoryList(data?.list || []);
    } catch (e) {
      showToast("error", e?.message || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  };
  const fetchCategoriesMaster = fetchCategories;

  // ADD (exactly mobile jaise – sirf name, koi image nahi)
  const addCategory = async () => {
    if (!categoryName.trim()) {
      showToast("error", "Category name is required");
      return;
    }
    if (!restaurantid) {
      showToast("error", "Restaurant not found. Please login again.");
      return;
    }
    try {
      setSubmitting(true);

      const res = await fetch(
        "https://qr.billsarthi.in/public/api/add/addproductcategory",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurantid,
            name: categoryName.trim(),
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Add failed");

      showToast("success", "Category added");
      setCategoryName("");
      setSheetOpen(false);
      // mobile code: add ke baad master list
      fetchCategoriesMaster();
    } catch (e) {
      showToast("error", e?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  // UPDATE (mobile jaisa /updateproductcategory)
  const updateCategory = async () => {
    const name = editName.trim();
    if (name.length < 2) {
      showToast("info", "Category name is too short");
      return;
    }
    if (!restaurantid || !selectedId) {
      showToast("error", "Something went wrong. Try again.");
      return;
    }
    try {
      setSubmitting(true);
      const res = await fetch(
        "https://qr.billsarthi.in/public/api/updateproductcategory",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurantid,
            productcategoryname: name,
            productcategoryid: selectedId,
            is_active: editStatus ? 1 : 0,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Update failed");

      showToast("success", "Category updated");
      setSheetOpen(false);
      // mobile code: update ke baad master list
      fetchCategoriesMaster();
    } catch (e) {
      showToast("error", e?.message || "Failed to update");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteCategory = async (id) => {
    if (!restaurantid) {
      showToast("error", "Restaurant not found. Please login again.");
      return;
    }
    try {
      const res = await fetch(
        "https://qr.billsarthi.in/public/api/deleteproductcategory",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid, productcategoryid: id }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Delete failed");
      showToast("success", "Category deleted");
      setConfirmId(null);
      fetchCategoriesMaster();
    } catch (e) {
      showToast("error", e?.message || "Delete failed");
    }
  };

  // -------- Handlers --------
  const openSheet = (mode = "add") => {
    setSheetMode(mode);
    if (mode === "add") {
      setCategoryName("");
    }
    setSheetOpen(true);
  };

  const closeSheet = () => {
    if (submitting) return;
    setSheetOpen(false);
  };

  const openEdit = (item) => {
    setSelectedId(item?.id);
    setEditName(item?.productcategoryname || "");
    setEditStatus(item?.is_active === 1 ? 1 : 0);
    openSheet("edit");
  };

  // ---------------------------------------------------------------------------
  return (
    <div style={sx.page}>
      <div style={sx.topBar}>
        <div>
          <div style={sx.title}>Product Categories</div>
          <div style={sx.subtitle}>Organize your menu like a pro</div>
        </div>
        <div style={sx.topActions}>
          <div style={sx.countPill}>
            Total: <b>{categoryList?.length || 0}</b>
          </div>
          <button onClick={fetchCategories} style={sx.secondaryBtn}>
            Refresh
          </button>
          <button onClick={() => openSheet("add")} style={sx.primaryBtn}>
            New Category
          </button>
        </div>
      </div>

      <div style={sx.toolsBar}>
        <input
          placeholder="Search category name…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={sx.searchInput}
        />
      </div>

      {/* Grid */}
      <div style={sx.grid}>
        {loading ? (
          <div style={sx.loaderBox}>Loading…</div>
        ) : filteredList.length === 0 ? (
          <div style={sx.emptyBox}>No categories found.</div>
        ) : (
          filteredList.map((item) => (
            <div
              key={item.id}
              style={{
                ...sx.card,
                borderColor:
                  item.is_active === 1
                    ? "rgba(1,91,154,.2)"
                    : "rgba(239,68,68,.2)",
              }}
            >
              <div style={sx.cardTop}>
                <div style={sx.meta}>
                  <div style={sx.name} title={item.productcategoryname}>
                    {item.productcategoryname}
                  </div>
                </div>
                <div
                  style={{
                    ...sx.badge,
                    backgroundColor:
                      item.is_active === 1 ? "#E6F4EA" : "#FEE2E2",
                    color: item.is_active === 1 ? "#166534" : "#B91C1C",
                    borderColor:
                      item.is_active === 1 ? "#B7E1C1" : "#FECACA",
                  }}
                >
                  {item.is_active === 1 ? "Active" : "Inactive"}
                </div>
              </div>

              <div style={sx.cardActions}>
                <button style={sx.smallBtn} onClick={() => openEdit(item)}>
                  Edit
                </button>
                <button
                  style={{
                    ...sx.smallBtn,
                    background: "#FEF2F2",
                    color: "#B91C1C",
                    border: "1px solid #FECACA",
                  }}
                  onClick={() => setConfirmId(item.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Bottom sheet: Add / Edit Category */}
      {sheetOpen && (
        <div style={sx.sheetOverlay} onClick={closeSheet}>
          <div style={sx.sheet} onClick={(e) => e.stopPropagation()}>
            <div style={sx.grabber} />
            <div style={sx.sheetHeader}>
              <div style={sx.sheetTitle}>
                {sheetMode === "add" ? "Add Category" : "Edit Category"}
              </div>
              <button onClick={closeSheet} style={sx.iconBtn}>
                ✕
              </button>
            </div>

            {/* Status row only in edit mode, same as mobile ka Active/Inactive */}
            {sheetMode === "edit" && (
              <div style={sx.statusRow}>
                <div style={sx.statusLeft}>
                  <span style={sx.statusLabel}>Active / Inactive</span>
                  <span
                    style={{
                      ...sx.statusBadge,
                      backgroundColor:
                        editStatus === 1 ? "#ECFDF5" : "#FEF2F2",
                      color:
                        editStatus === 1 ? "#059669" : "#DC2626",
                      borderColor:
                        editStatus === 1 ? "#A7F3D0" : "#FECACA",
                    }}
                  >
                    {editStatus === 1 ? "Active" : "Inactive"}
                  </span>
                </div>
                {/* Simple inline toggle – koi external CSS nahi chahiye */}
                <div
                  style={sx.toggleWrap}
                  onClick={() =>
                    setEditStatus((prev) => (prev === 1 ? 0 : 1))
                  }
                >
                  <div
                    style={{
                      ...sx.toggleTrack,
                      backgroundColor:
                        editStatus === 1 ? "#4f46e5" : "#E5E7EB",
                    }}
                  >
                    <div
                      style={{
                        ...sx.toggleThumb,
                        transform:
                          editStatus === 1
                            ? "translateX(18px)"
                            : "translateX(0px)",
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            <div style={sx.formRow}>
              <label style={sx.label}>Category Name</label>
              <input
                style={sx.input}
                type="text"
                placeholder="e.g., Starters"
                value={sheetMode === "add" ? categoryName : editName}
                onChange={(e) =>
                  sheetMode === "add"
                    ? setCategoryName(e.target.value)
                    : setEditName(e.target.value)
                }
                maxLength={30}
              />
              <div style={sx.hint}>
                Max 30 characters. Keep it short and clear.
              </div>
            </div>

            <button
              onClick={sheetMode === "add" ? addCategory : updateCategory}
              disabled={submitting}
              style={{
                ...sx.primaryBtnFull,
                opacity: submitting ? 0.8 : 1,
              }}
            >
              {submitting
                ? sheetMode === "add"
                  ? "Saving…"
                  : "Updating…"
                : sheetMode === "add"
                ? "Save Category"
                : "Update Category"}
            </button>
          </div>
        </div>
      )}

      {/* Confirm delete modal */}
      {confirmId !== null && (
        <div style={sx.modalOverlay}>
          <div style={sx.modal}>
            <div style={sx.modalTitle}>Delete Category</div>
            <div style={sx.modalText}>
              This action cannot be undone. Continue?
            </div>
            <div style={sx.modalRow}>
              <button
                style={sx.cancelBtn}
                onClick={() => setConfirmId(null)}
              >
                Cancel
              </button>
              <button
                style={sx.dangerBtn}
                onClick={() => deleteCategory(confirmId)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div style={sx.loadingOverlay}>
          <div style={sx.spinner} />
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          style={{
            ...sx.toast,
            background:
              toast.type === "success"
                ? "#16a34a"
                : toast.type === "error"
                ? "#b91c1c"
                : "#0ea5e9",
          }}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default AddCategory;

/* ==================== styles ==================== */
const sx = {
  page: {
    minHeight: "100vh",
    background: "#F6F8FB",
    fontFamily:
      "Inter, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
    padding: "18px",
  },

  /* Top bar */
  topBar: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 12,
    background: "#fff",
    borderRadius: 16,
    padding: "14px 16px",
    border: "1px solid #E5E7EB",
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
    flexWrap: "wrap",
  },
  title: { fontSize: 18, fontWeight: 900, lineHeight: 1.1, color: "#0F172A" },
  subtitle: { fontSize: 12, color: "#64748B", marginTop: 4 },
  topActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  countPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 999,
    background: "#F1F5F9",
    border: "1px solid #E2E8F0",
    fontWeight: 800,
    color: "#0F172A",
  },
  secondaryBtn: {
    background: "#F1F5F9",
    color: "#0F172A",
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid #E2E8F0",
    fontWeight: 800,
    cursor: "pointer",
  },
  primaryBtn: {
    background: BRAND,
    color: "#fff",
    padding: "10px 14px",
    borderRadius: 12,
    border: "none",
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 8px 18px rgba(1,91,154,0.2)",
  },

  /* Tools */
  toolsBar: {
    marginTop: 14,
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  searchInput: {
    border: "1px solid #E5E7EB",
    outline: "none",
    background: "#fff",
    width: "100%",
    maxWidth: 460,
    color: "#0F172A",
    fontSize: 14,
    padding: "12px 14px",
    borderRadius: 12,
    boxShadow: "0 1px 6px rgba(15, 23, 42, 0.06)",
  },

  /* Grid */
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: 12,
    marginTop: 14,
  },
  loaderBox: {
    background: "#fff",
    padding: 18,
    borderRadius: 14,
    border: "1px solid #E5E7EB",
    textAlign: "center",
    color: "#334155",
    fontWeight: 700,
  },
  emptyBox: {
    background: "#fff",
    padding: 18,
    borderRadius: 14,
    border: "1px solid #E5E7EB",
    textAlign: "center",
    color: "#64748B",
    fontWeight: 700,
  },

  card: {
    background: "#fff",
    borderRadius: 16,
    border: "1px solid rgba(17,24,39,0.06)",
    padding: 12,
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
  },
  cardTop: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    justifyContent: "space-between",
  },
  meta: { flex: 1, minWidth: 0 },
  name: {
    fontWeight: 900,
    color: "#0F172A",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  badge: {
    marginTop: 4,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    border: "1px solid transparent",
  },
  cardActions: {
    marginTop: 10,
    display: "flex",
    gap: 8,
  },
  smallBtn: {
    flex: 1,
    borderRadius: 10,
    padding: "8px 10px",
    border: "1px solid #E5E7EB",
    background: "#F8FAFC",
    fontWeight: 800,
    cursor: "pointer",
  },

  /* Bottom sheet */
  sheetOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.35)",
    display: "flex",
    justifyContent: "flex-end",
    zIndex: 50,
  },
  sheet: {
    background: "#fff",
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    padding: 16,
    maxHeight: "92vh",
    overflowY: "auto",
    boxShadow: "0 -6px 24px rgba(0,0,0,.1)",
    width: "100%",
    maxWidth: 520,
    margin: "0 auto",
  },
  grabber: {
    width: 44,
    height: 4,
    background: "#CBD5E1",
    borderRadius: 99,
    margin: "0 auto 10px",
  },
  sheetHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  sheetTitle: { fontSize: 16, fontWeight: 900, color: "#0F172A" },
  iconBtn: {
    background: "#F1F5F9",
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    padding: "6px 10px",
    cursor: "pointer",
  },

  formRow: { marginTop: 12 },
  label: {
    fontWeight: 700,
    color: "#0F172A",
    marginBottom: 6,
    display: "block",
  },
  input: {
    width: "100%",
    border: "1px solid #E2E8F0",
    borderRadius: 12,
    padding: "10px 12px",
    background: "#F8FAFC",
    color: "#0F172A",
    outline: "none",
  },
  hint: { fontSize: 12, color: "#64748B", marginTop: 6 },

  primaryBtnFull: {
    marginTop: 16,
    width: "100%",
    height: 48,
    borderRadius: 14,
    background: BRAND,
    color: "#fff",
    fontWeight: 900,
    border: "none",
    cursor: "pointer",
  },

  // status row for edit sheet
  statusRow: {
    marginTop: 10,
    padding: "10px 12px",
    borderRadius: 12,
    background: "#F9FAFB",
    border: "1px solid #E5E7EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  statusLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  statusLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: "#374151",
  },
  statusBadge: {
    padding: "4px 9px",
    borderRadius: 999,
    border: "1px solid transparent",
    fontSize: 12,
    fontWeight: 700,
  },
  toggleWrap: {
    display: "inline-flex",
    alignItems: "center",
    cursor: "pointer",
  },
  toggleTrack: {
    width: 42,
    height: 22,
    borderRadius: 999,
    background: "#E5E7EB",
    position: "relative",
    transition: "background 0.2s ease",
  },
  toggleThumb: {
    position: "absolute",
    top: 3,
    left: 3,
    width: 16,
    height: 16,
    borderRadius: "999px",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(0,0,0,.2)",
    transition: "transform 0.2s ease",
  },

  /* Confirm Modal */
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 60,
  },
  modal: {
    width: 360,
    maxWidth: "92vw",
    background: "#fff",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 10px 30px rgba(0,0,0,.2)",
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 900,
    color: "#0F172A",
    textAlign: "center",
  },
  modalText: {
    color: "#475569",
    textAlign: "center",
    marginTop: 8,
    marginBottom: 12,
  },
  modalRow: {
    display: "flex",
    gap: 10,
    justifyContent: "space-between",
  },
  cancelBtn: {
    flex: 1,
    height: 44,
    borderRadius: 12,
    background: "#F8FAFC",
    border: "1px solid #E5E7EB",
    fontWeight: 800,
    cursor: "pointer",
  },
  dangerBtn: {
    flex: 1,
    height: 44,
    borderRadius: 12,
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    color: "#B91C1C",
    fontWeight: 900,
    cursor: "pointer",
  },

  /* Loading overlay */
  loadingOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(255,255,255,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    zIndex: 40,
  },
  spinner: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    border: "3px solid #cfe6f5",
    borderTopColor: BRAND,
    animation: "spin 1s linear infinite",
  },

  /* Toast */
  toast: {
    position: "fixed",
    bottom: 22,
    left: "50%",
    transform: "translateX(-50%)",
    color: "#fff",
    padding: "10px 14px",
    borderRadius: 12,
    fontWeight: 800,
    boxShadow: "0 10px 20px rgba(0,0,0,.2)",
    zIndex: 70,
  },
};
